/**
 * Floating bottom-right mini-player that takes over once the user navigates
 * away from /watch. Click to expand back, X to dismiss.
 *
 * The underlying <video> element + media engine are owned by a module-level
 * singleton inside Player.tsx; this component just relocates that element
 * into the floating frame. So:
 *   - Paused-stays-paused across navigation.
 *   - Playing-keeps-playing across navigation (no reconnect blip).
 *   - The X button is the only thing that actually tears the engine down.
 */
import { useEffect, useState } from "react";
import { Maximize2, X } from "lucide-react";
import { useIsPhone } from "../lib/useIsPhone";
import { useLocation, useNavigate } from "react-router-dom";
import { Player, destroyPlayer, isUserPaused } from "./Player";
import { Playlists } from "../lib/api";
import { usePlayback } from "../contexts/PlaybackContext";

export function MiniPlayer() {
  const playback = usePlayback();
  const { source, clear, reportPosition, seekTime, setSeekTime, setSource, casting } = playback;
  const loc = useLocation();
  const navigate = useNavigate();
  // MUST stay above every early return below. This was called after them, so
  // closing the mini player (source -> null) returned before the hook ran, React
  // counted fewer hooks than the previous render, and the app crashed with
  // "Minified React error #300". Hooks run unconditionally or not at all.
  const isPhone = useIsPhone();

  // Hide the mini-player when the user is using Picture-in-Picture: two
  // floating windows competing for the same content is just confusing.
  const [pipActive, setPipActive] = useState<boolean>(
    () => typeof document !== "undefined" && !!document.pictureInPictureElement
  );
  useEffect(() => {
    const onPip = () => setPipActive(!!document.pictureInPictureElement);
    document.addEventListener("enterpictureinpicture", onPip, true);
    document.addEventListener("leavepictureinpicture", onPip, true);
    return () => {
      document.removeEventListener("enterpictureinpicture", onPip, true);
      document.removeEventListener("leavepictureinpicture", onPip, true);
    };
  }, []);

  // Mini-player scrub: rebuild the URL according to the source's seekMode and
  // republish via setSource. PlaybackContext's contentKey check keeps this
  // from being treated as a "different content" event, so the engine just
  // reloads in place.
  const onSeek = source && source.seekMode !== "native"
    ? (target: number) => {
        const t = Math.max(0, target);
        setSeekTime(t);
        if (source.seekMode === "reload-transmux" && source.baseProxyUrl) {
          const sep = source.baseProxyUrl.includes("?") ? "&" : "?";
          const newUrl = `${source.baseProxyUrl}${sep}seek=${t.toFixed(3)}`;
          setSource({ ...source, url: newUrl });
        } else if (source.seekMode === "reload-catchup" && source.catchupCtx) {
          const cc = source.catchupCtx;
          const shifted = cc.origStartMs + Math.floor(t * 1000);
          const remaining = Math.max(1, cc.durationMin - Math.floor(t / 60));
          Playlists.catchupUrl(cc.playlistId, cc.streamId, shifted, remaining)
            .then((d) => setSource({ ...source, url: d.proxy_url }))
            .catch(() => { /* leave seekTime as-is, engine stays on old URL */ });
        }
      }
    : undefined;

  if (!source) return null;
  if (loc.pathname.startsWith("/watch")) return null;
  if (loc.pathname === "/login" || loc.pathname === "/change-password") return null;

  // A deliberately paused stream should not follow you around the app. It used
  // to reappear here AND resume itself, which is the opposite of what pausing
  // asked for.
  if (isUserPaused()) return null;
  if (pipActive) return null;  // user already has a floating window via PiP
  if (casting) return null;    // playback is on the TV — local mini would just confuse

  return (
    <div
      className={
        isPhone
          ? // Docked bar sitting on the tab bar: full width, video reduced to a
            // 16:9 thumb. A floating 360px card covers a third of a 390px
            // screen and lands under the thumb on the way to the tab bar.
            "fixed z-50 left-0 right-0 bottom-[calc(env(safe-area-inset-bottom)+56px)] flex items-stretch gap-3 border-t border-line bg-elevated shadow-e3 pr-2"
          : "fixed z-50 right-3 bottom-3 w-[min(360px,90vw)] rounded-card overflow-hidden shadow-e4 border border-line-strong bg-canvas"
      }
    >
      <div className={isPhone ? "relative w-[112px] flex-shrink-0" : "relative"}>
        <Player
          url={source.url}
          altUrl={source.altUrl ?? null}
          engine={source.engine}
          altEngine={source.altEngine}
          poster={source.poster ?? null}
          title={source.title}
          autoPlay={false}
          isLive={source.isLive}
          forceDuration={source.forceDuration ?? null}
          baseTime={seekTime || 0}
          onSeek={onSeek}
          castUrl={source.castUrl ?? null}
          subtitles={source.subtitles}
          chrome={!isPhone}
          onProgress={(t) => reportPosition(t)}
        />
        <div className={isPhone ? "hidden" : "absolute top-1 right-1 flex gap-1"}>
          <button
            onClick={() => navigate(source.href)}
            className="w-7 h-7 rounded-full bg-canvas/80 backdrop-blur-sm text-text-primary hover:text-accent-gold flex items-center justify-center"
            aria-label="Expand"
            title="Expand"
          >
            <Maximize2 size={14} />
          </button>
          <button
            onClick={() => {
              destroyPlayer();
              clear();
            }}
            className="w-7 h-7 rounded-full bg-canvas/80 backdrop-blur-sm text-text-primary hover:text-danger flex items-center justify-center"
            aria-label="Close mini player"
            title="Close"
          >
            <X size={14} />
          </button>
        </div>
      </div>
      <div
        className={
          isPhone
            ? "flex-1 min-w-0 flex items-center text-[13px] font-ui text-text-primary"
            : "px-3 py-1.5 text-[11px] font-ui text-text-secondary truncate"
        }
      >
        <span className="truncate">
          {source.isLive && (
            /* Live is a signal state — the label used to be danger red while
               its own dot was gold, which read as an error on every channel. */
            <span className="inline-flex items-center gap-1 mr-1.5 text-accent-gold font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-gold animate-live-pulse" />
              Live
            </span>
          )}
          {source.title}
        </span>
      </div>
      {isPhone && (
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={() => navigate(source.href)}
            className="w-11 h-11 flex items-center justify-center text-text-secondary hover:text-accent-gold"
            aria-label="Expand"
          >
            <Maximize2 size={18} />
          </button>
          <button
            onClick={() => {
              destroyPlayer();
              clear();
            }}
            className="w-11 h-11 flex items-center justify-center text-text-secondary hover:text-danger"
            aria-label="Close mini player"
          >
            <X size={18} />
          </button>
        </div>
      )}
    </div>
  );
}
